/**
 * How a worker is named, for every tab in the hunt panel.
 *
 * The fleets number into ONE index space — the schedule watch takes
 * `0 .. watch-1`, the shift list the band above it, and the GA fleet everything
 * above that — so a bare worker number cannot say which fleet it came from.
 * Most rows in the panel carry nothing else: a poll record is `{worker: 6}` and
 * no more. Naming one is therefore either a lookup of a role the server stamped
 * on the row, or arithmetic against those two boundaries.
 *
 * Pure and import-free on purpose, split out of `hunt-tabs.tsx` so the arithmetic
 * can be exercised without a renderer — see `hunt-fleet.check.ts`. It got split
 * out the day a wrong answer shipped: with the shift list turned off, every
 * worker above the watch was tagged `s`, and the tabs reading the role off the
 * row called those same workers something else. It was a one-line boundary
 * mistake sitting in the middle of three thousand lines of table markup, and
 * nothing could reach it to say so.
 */

/**
 * The wire vocabulary. `shift-list` is the endpoint's fleet; internally it is
 * `gql`. `ga` is `get-all-schedules`, the fleet that arms beside the watch
 * while a shift has no UUID, to read it off the listing — and that used to run
 * alone, BEFORE an application existed, on runs retained from the old
 * availability wait.
 *
 * `job` is RETIRED. The job-record fleet no longer runs and no live hunt arms
 * one, but stored records — sightings, strikes and worker rows from runs that
 * predate its removal — still carry the role, and a post-mortem has to render
 * those rows rather than fall over on them. It has no band in the index space
 * below: a row is only ever named `j…` from a role the server stamped on it.
 */
export type WorkerRole = 'watch' | 'shift-list' | 'job' | 'ga'

/**
 * The letter for each fleet. One place, so a tag can never disagree with a tab.
 *
 * `ga` is two characters where the others are one, deliberately. The tag is read,
 * clicked and grepped rather than aligned in a column, and `ga14` says which
 * fleet it is without a legend — which matters more for the one fleet an
 * operator meets before the hunt has started.
 */
const ROLE_LETTER: Record<WorkerRole, string> = {
  watch: 'w',
  'shift-list': 's',
  job: 'j',
  ga: 'ga',
}

/**
 * The spoken name for each fleet, for labels a screen reader reads aloud.
 *
 * Beside `ROLE_LETTER` and keyed the same way so the two cannot drift. This was a
 * `role === 'shift-list' ? … : 'Schedule'` binary written when there were two
 * fleets, and the next one silently inherited the else — a chip reading `j7`
 * announced itself as "Schedule worker 7" to the one reader who cannot see the
 * `j`. A `Record<WorkerRole, …>` makes adding a fleet a type error here rather
 * than a wrong word in an aria-label nobody looks at.
 */
const ROLE_NAME: Record<WorkerRole, string> = {
  watch: 'Schedule',
  'shift-list': 'Shift-list',
  job: 'Job',
  /*
   * Not "Schedules", which is what the endpoint reads and what everyone will
   * want to call it. The watch fleet is already named Schedule, and two fleets
   * sharing a name in a worker table and a sighting chip is worse than an
   * abbreviation nobody has to disambiguate.
   */
  ga: 'GA',
}

/**
 * `w3` for a schedule worker, `s6` for a shift-list one, `ga11` for a GA one.
 *
 * The NUMBER stays global on purpose. It is the Redis log slot and the console's
 * `?worker=` focus parameter, so renumbering each fleet from zero would produce a
 * label matching nothing a reader can click, query or find in a log line — two
 * workers would both be `s0` and neither would be slot 0. Only the letter carries
 * the fleet.
 *
 * Preferred over `fleetTag` wherever a row HAS a role: the server derived it with
 * the same `roleOf` that dispatched the work, so it cannot disagree with the
 * fleet counts — and it still reads correctly in a post-mortem, where a retained
 * snapshot may have lost them. It is also the only way a retired `job` row is
 * ever named.
 *
 * A row with no role at all is a snapshot retained before the field shipped. It
 * reads as `watch`, which is what the whole fleet was when that snapshot was
 * written.
 */
export function roleTag(w: { worker: number; role?: WorkerRole }): string {
  return `${ROLE_LETTER[w.role ?? 'watch']}${w.worker}`
}

/** The fleet's name in prose. The same fact as `roleTag`, spelled out. */
export function roleName(role?: WorkerRole): string {
  return ROLE_NAME[role ?? 'watch']
}

/**
 * The same name, for a record that carries only its worker number.
 *
 * ── The two undefineds are not the same undefined ───────────────────
 * A missing `watch` is genuinely unknown — a snapshot retained before the count
 * shipped — and everything reads as `w`, which is what the whole fleet was then.
 *
 * A missing `gql` is a ZERO. The payload ships `gql: null` exactly when
 * `gqlCount === 0`, so the panel's `view.gql?.workers` is undefined precisely
 * when no shift-list fleet was armed. That is a fact, not a gap, and reading it
 * as "boundary unknown" is what once tagged every worker above the watch `s` on
 * the tabs that cannot read a role. A zero-width band must COLLAPSE, so the
 * number falls through to the fleet that actually holds it.
 */
export function fleetTag(worker: number, watch?: number, gql?: number): string {
  return `${ROLE_LETTER[fleetRole(worker, watch, gql)]}${worker}`
}

/**
 * The same arithmetic as `fleetTag`, answered as a role rather than a label.
 *
 * For rows that must be GROUPED rather than merely named — the Active tab splits
 * its sightings by fleet, and a filter that put a GA sighting under Schedule
 * would be a worse lie than a mislabelled chip, because the row then simply is
 * not where somebody went looking for it.
 *
 * Prefer the role the server stamped on the row wherever there is one; this is
 * the fallback for records written before that field shipped. Both undefineds
 * mean what they mean in `fleetTag`, and for the same reasons: a missing `watch`
 * is genuinely unknown and everything falls to `watch`, while a missing `gql` is
 * a zero-width band that must collapse rather than swallow the fleet above it.
 *
 * Everything above the shift-list band is GA. That case is not hypothetical:
 * snapshots retained from the old availability wait armed the GA fleet alone,
 * so `watch` and `gql` are both zero and every worker belongs to the fleet at
 * the top. Reading a missing `gql` as "boundary unknown" would tag every GA
 * worker `s`, which is the exact bug this file was split out for.
 */
export function fleetRole(worker: number, watch?: number, gql?: number): WorkerRole {
  if (watch === undefined || worker < watch) return 'watch'
  if (worker < watch + (gql ?? 0)) return 'shift-list'
  return 'ga'
}
